import React from "react";
import {
  Box,
  Heading,
  Text,
  useColorModeValue,
  VStack,
  Skeleton,
  HStack,
  Icon,
} from "@chakra-ui/react";
import { Trans } from "@lingui/react/macro";
import {
  FaBatteryEmpty,
  FaBatteryFull,
  FaBatteryHalf,
  FaBatteryQuarter,
  FaBatteryThreeQuarters,
} from "react-icons/fa";
import { IDevice } from "../../types/deviceInterface";

const getBatteryStatus = (voltage: number) => {
  const icon =
    voltage <= 2.1
      ? FaBatteryEmpty
      : voltage <= 2.25
        ? FaBatteryQuarter
        : voltage <= 2.5
          ? FaBatteryHalf
          : voltage <= 2.75
            ? FaBatteryThreeQuarters
            : FaBatteryFull;

  return {
    icon,
    color: voltage <= 2.1 ? "red.500" : voltage <= 2.5 ? "orange.400" : "green.500",
  };
};

const DataDump = ({ device }: { device: IDevice | undefined }) => {
  const timestampColor = useColorModeValue("gray.600", "gray.400");

  if (!device) {
    return (
      <>
        <Skeleton height="20px" />
        <Skeleton height="20px" />
        <Skeleton height="20px" />
        <Skeleton height="20px" />
        <Skeleton height="20px" />
        <Skeleton height="20px" />
      </>
    );
  }
  if (Object.keys(device.data).length === 0) {
    return (
      <Text>
        <Trans>Current data unavailable</Trans>
      </Text>
    );
  }

  // Convert timestamp string to Date object and format it
  const timestamp = new Date(device.data.timestamp).toLocaleString("default", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  const isTracker = device.device_type === 9;
  const batteryVoltage = device.data.battery_voltage;
  const batteryStatus =
    batteryVoltage !== null && batteryVoltage !== undefined
      ? getBatteryStatus(batteryVoltage)
      : null;

  return (
    <>
      {!isTracker &&
        device.data.soil_moisture !== null &&
        device.data.soil_moisture !== undefined && (
          <Text>
            <Trans>Soil Moisture:</Trans> {device.data.soil_moisture.toFixed(1)} %{" "}
          </Text>
        )}
      {!isTracker &&
        device.data.soil_moisture_2 !== null &&
        device.data.soil_moisture_2 !== undefined && (
          <Text>
            <Trans>Soil Moisture 2:</Trans> {device.data.soil_moisture_2.toFixed(1)} %{" "}
          </Text>
        )}
      {!isTracker &&
        device.data.soil_moisture_3 !== null &&
        device.data.soil_moisture_3 !== undefined && (
          <Text>
            <Trans>Soil Moisture 3:</Trans> {device.data.soil_moisture_3.toFixed(1)} %{" "}
          </Text>
        )}
      {!isTracker &&
        device.data.soil_conductivity !== null &&
        device.data.soil_conductivity !== undefined && (
          <Text>
            <Trans>Soil Conductivity:</Trans> {device.data.soil_conductivity.toFixed(0)}
          </Text>
        )}
      {!isTracker &&
        device.data.soil_conductivity_2 !== null &&
        device.data.soil_conductivity_2 !== undefined && (
          <Text>
            <Trans>Soil Conductivity 2:</Trans> {device.data.soil_conductivity_2.toFixed(0)}
          </Text>
        )}
      {!isTracker &&
        device.data.soil_conductivity_3 !== null &&
        device.data.soil_conductivity_3 !== undefined && (
          <Text>
            <Trans>Soil Conductivity 3:</Trans> {device.data.soil_conductivity_3.toFixed(0)}
          </Text>
        )}
      {!isTracker &&
        device.data.soil_temperature !== null &&
        device.data.soil_temperature !== undefined && (
          <Text>
            <Trans>Soil Temperature:</Trans> {device.data.soil_temperature.toFixed(1)} ℃
          </Text>
        )}
      {device.data.air_temperature !== null && device.data.air_temperature !== undefined && (
        <Text>
          <Trans>Air temperature:</Trans> {device.data.air_temperature.toFixed(1)} ℃
        </Text>
      )}
      {device.data.humidity !== null && device.data.humidity !== undefined && (
        <Text>
          <Trans>Humidity:</Trans> {device.data.humidity.toFixed(0)} %
        </Text>
      )}
      {device.data.lux !== null && device.data.lux !== undefined && (
        <Text>
          <Trans>Lux:</Trans> {device.data.lux.toFixed(0)}
        </Text>
      )}
      {batteryStatus && batteryVoltage !== undefined && (
        <HStack spacing={1}>
          <Icon as={batteryStatus.icon} color={batteryStatus.color} boxSize={7} />
          <Text fontSize="sm" fontWeight="light">
            {batteryVoltage.toFixed(2)} V
          </Text>
        </HStack>
      )}
      <Text color={timestampColor} fontSize="sm">
        <Trans>Last Updated:</Trans> {timestamp}
      </Text>
    </>
  );
};

export { DataDump };
